import 'reflect-metadata';

import Link from 'next/link';
import Router from 'next/router';
import { Component } from 'react';
import InternalMotiveComponent from '../../components/ui/motive/internal-motive.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import { lazyInject } from '../../context/module';
import { ApiConfig } from '../../services/api-config';
import { EnvironmentService } from '../../services/environment.service';
import { LightHttp } from '../../services/light-http.service';
import { MotivesService } from '../../services/motives.service';
import { ScriptLoadService } from '../../services/script-load.service';
import { WebSocketService } from '../../services/websocket.service';
import { WindowService } from '../../services/window.service';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { FeatureSearchBox } from './feature-search-box/feature-search-box.component';
import { PageSelectionDropdown } from './feature-select-dropdown/feature-select-dropdown.component';
import { pageCopy, RichText } from '../ui/page-copy/page-copy';
import styles from './home.component.module.scss';

// types.ts


// Define the interfaces for props and state
interface HomeComponentState {
    selectedFormat: string | null;
    selectedPageURL: string | null;
    statsLoaded: boolean;
    globalStats: {
        workflowsCompleted?: number;
        uploadsCount?: number;
        uploadBytes?: number;
        downloadsCount?: number;
        downloadBytes?: number;
    };
}


export default class HomeComponent extends Component<any, HomeComponentState> {
    showHistory = true;
    siteHistory = '';
    effectiveMotives: MotiveConfig;

    internalAdsEnabled: boolean;
    isDomainSupportOfflineProcessing: boolean;

    @lazyInject(EnvironmentService) envService: EnvironmentService;
    @lazyInject(WindowService) public windowService: WindowService;
    @lazyInject(ScriptLoadService) private scriptLoader: ScriptLoadService;
    @lazyInject(MotivesService) private motivesService: MotivesService;
    @lazyInject(WebSocketService) private webSocketService: WebSocketService;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;
    @lazyInject(LightHttp) private lightHttp: LightHttp;

    readonly state: Readonly<HomeComponentState> = this.initializeFeatureDataAndGetState();
    constructor(props) {
        super(props);
        this.motivesService.onEffectiveMotiveSettingsChange.subscribe(
            (motives) => {
                this.effectiveMotives = motives;
                this.internalAdsEnabled = this.effectiveMotives && this.effectiveMotives.internalMotivesEnabled;
            },
            (error) => console.error(error),
        );

        if (this.envService.env.showHistory) {
            this.siteHistory = props.history;
        }
    }

    initializeFeatureDataAndGetState(): HomeComponentState {
        if (BrowserUtils.hasWindow()) {
            this.fetchGlobalStats();
        }
        return {
            selectedFormat: '7Z', // Set an initial selected value
            selectedPageURL: null,
            statsLoaded: false,
            globalStats: {},
        };
    }

    private fetchGlobalStats() {
        try {
            this.lightHttp.post<any>(this.apiConfig.getGlobalStatsEndpoint(), {}).subscribe({
                next: (data) => {
                    this.setState({ globalStats: data, statsLoaded: true });
                },
                error: () => {
                    this.setState({ statsLoaded: false });
                },
            });
        } catch (e) {
            // SSR or service not available
        }
    }

    private formatNumber(num: number | undefined): string {
        if (num === undefined || num === null) return '0';
        return num.toLocaleString();
    }

    private formatBytes(bytes: number | undefined): string {
        if (bytes === undefined || bytes === null || bytes === 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + units[i];
    }

    navigateToLink(link) {
        if (BrowserUtils.hasWindow()) {
            // const router = useRouter();
            Router.push(link);
        }
    }

    handleFormatChange = (newValue: string | null) => {
        this.setState({ selectedFormat: newValue });
    };

    handleFeatureSelectEvent(selectedPageURL: string | null) {
        this.setState({ selectedPageURL: selectedPageURL });
        Router.push(selectedPageURL);
    }

    /** page_seo.title with its `page_content.h1Highlight` phrase in the gradient span. */
    private heading(title: string, highlight?: string) {
        const at = highlight ? title.indexOf(highlight) : -1;
        if (at < 0) return title;
        return <>{title.slice(0, at)}<span className={styles['text-gradient']}>{highlight}</span>{title.slice(at + highlight.length)}</>;
    }

    render() {
        // Page copy = SSR page data (xconvert-web-ssr sources/page-data-overrides/index.json → root.json): H1 and
        // lead = page_seo, the quick links, category cards, "why" cards and privacy line = page_content. The search,
        // dropdown, divider label and live stats labels are UI here.
        const { seo, content: c } = pageCopy(this.props);
        const categories = c.categories || {};
        const why = c.why || {};
        const card = `card h-100 border-0 rounded-4 shadow-sm p-4 text-center hover-lift-shadow ${styles['hover-lift-shadow']} text-decoration-none`;
        return <div>
            <div className="gcontent-container">
                <MotiveComponent
                    data={AdSlots.pageTop} containerWidth="100%" containerHeight="90px"
                    location={MotiveLocationTypes.homePageTop}
                    adWidth="970px"
                    adHeight="90px" showAds={true} className='width_major'
                >
                </MotiveComponent>
                <InternalMotiveComponent
                    internalMotiveId="728"
                    containerWidth="100%" containerHeight="90px" adWidth="970px" adHeight="90px" showAds={true}
                ></InternalMotiveComponent>
            </div>
            <section className="py-5 py-md-5">
                <div className="container text-center">
                    <h1 className="display-4 fw-bolder text-dark lh-sm">
                        {this.heading(seo.title || '', c.h1Highlight)}
                    </h1>
                    <p className="mt-3 mx-auto lead text-secondary" style={{ maxWidth: '700px' }}>
                        {seo.description}
                    </p>
                    <FeatureSearchBox />
                    <div className={styles.divider}>or browse by category</div>
                    <PageSelectionDropdown
                        options={this.props.selectorOptions}
                        pageUrl={this.state.selectedPageURL}
                        onChange={(v) => this.setState({ selectedPageURL: v })}
                        onSubmit={(v) => this.handleFeatureSelectEvent(v)}>
                    </PageSelectionDropdown>
                    {(c.quickLinks || []).map((row: any[], r: number) => <div key={r} className={styles['quick-links']}>
                        {row.map(l => <Link key={l.href} href={l.href} className={styles['quick-link']}>{l.text}</Link>)}
                    </div>)}
                </div>
            </section>

            <section id="tools" className="py-5 py-md-5 bg-light">
                <div className="container">
                    <div className="text-center">
                        <h2 className="fs-2 fw-bold text-dark">{categories.title}</h2>
                        <p className="mt-3 mx-auto lead text-secondary" style={{ maxWidth: '600px' }}>
                            {categories.subtitle}
                        </p>
                    </div>
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-4 mt-5">
                        {(categories.cards || []).map((t: any) => <div key={t.href} className="col">
                            <Link href={t.href} className={card}>
                                <span className="fs-1 mb-3">{t.icon}</span>
                                <h3 className="fs-5 fw-bold text-dark">{t.title}</h3>
                                <p className="small text-secondary mt-2">{t.text}</p>
                            </Link>
                        </div>)}
                    </div>
                    {categories.allTools && <div className="text-center mt-5">
                        <Link href={categories.allTools.href} className="text-xconvert-blue-dark fw-semibold text-decoration-none hover-underline">{categories.allTools.text}</Link>
                    </div>}
                </div>
            </section>

            <section id="why-us" className="py-5 py-md-5">
                <div className="container">
                    <div className="text-center">
                        <h2 className="fs-2 fw-bold text-dark">{why.title}</h2>
                        <p className="mt-3 mx-auto lead text-secondary" style={{ maxWidth: '600px' }}>
                            {why.subtitle}
                        </p>
                    </div>
                    <div className="row row-cols-1 row-cols-md-3 g-4 mt-5">
                        {(why.cards || []).map((w: any, i: number) => <div key={i} className="col">
                            <div className="text-center p-4">
                                <span className="fs-1 text-xconvert-blue-dark">{w.icon}</span>
                                <h3 className="fs-5 fw-bold text-dark mt-3">{w.title}</h3>
                                <p className="small text-secondary mt-2">{w.text}</p>
                            </div>
                        </div>)}
                    </div>
                </div>
            </section>

            <section id="security" className="py-5 py-md-5 bg-dark text-white">
                <div className="container text-center">
                    <h2 className="fs-2 fw-bold">{c.trust?.title}</h2>
                    {this.state.statsLoaded ? (
                        <div className="row row-cols-1 row-cols-md-3 g-4 mt-4">
                            <div className="col">
                                <div className="fs-1 fw-bolder">{this.formatNumber(this.state.globalStats.workflowsCompleted)}</div>
                                <p className="text-light mt-1">Files Processed</p>
                            </div>
                            <div className="col">
                                <div className="fs-1 fw-bolder">{this.formatNumber(this.state.globalStats.uploadsCount)}</div>
                                <p className="text-light mt-1">Files Uploaded ({this.formatBytes(this.state.globalStats.uploadBytes)})</p>
                            </div>
                            <div className="col">
                                <div className="fs-1 fw-bolder">{this.formatNumber(this.state.globalStats.downloadsCount)}</div>
                                <p className="text-light mt-1">Files Downloaded ({this.formatBytes(this.state.globalStats.downloadBytes)})</p>
                            </div>
                        </div>
                    ) : (
                        <div className="row row-cols-1 row-cols-md-3 g-4 mt-4">
                            {[0, 1, 2].map(i => (
                                <div className="col" key={i}>
                                    <div className="placeholder-glow">
                                        <span className="placeholder col-6 fs-1 rounded" style={{ height: '48px', display: 'inline-block' }}></span>
                                    </div>
                                    <p className="text-light mt-1 placeholder-glow"><span className="placeholder col-4 rounded"></span></p>
                                </div>
                            ))}
                        </div>
                    )}
                    {this.state.statsLoaded && <p className="text-secondary small mt-2 mb-0">Stats are updated every minute</p>}
                    <div className="mt-5 mx-auto border-top border-secondary pt-4" style={{ maxWidth: '600px' }}>
                        <RichText className="lead text-light" html={c.trust?.privacyHtml} />
                    </div>
                </div>
            </section>
        </div>
    }
}
